import deepMerge from 'deepmerge';
import noop from 'lodash/noop';
import { v4 as uuid } from 'uuid';

/**
 * @template P
 * @template PayloadKeys
 * @typedef {IfVoid<P,P,{ payload: PayloadKeys extends any[] ? { [K in PayloadKeys[number]]: P[IndexOf<PayloadKeys,K,[]>] } : P }>} ActionPayload
 */

/** @type {() => ActionPayload<void,void>} */
export const noParameters = noop;
/** @type {<P extends any>(payload: P) => ActionPayload<P,void>} */
export const singleParameter = payload => ({
    payload,
});
/** @type {<T extends string[]>(...names: T) => <P extends any[]>(...params: P) => ActionPayload<P,T>} */
export const namedParameters = (...names) => {
    if (names.some(name => typeof name !== 'string')) {
        throw new Error(`Redux Utils :: 'namedParameters' arguments must be of type 'String'`);
    }
    return (...params) => ({
        payload: names.reduce((payload, name, index) => {
            return {
                ...payload,
                [name]: params[index],
            };
        }, {}),
    });
};

/** @type {<G extends (...args: P) => R>(getPayload: G) => <M extends object | void, P extends any[] | void = void>(meta?: M, ...params: P) => { meta: M & { asyncId: string } } & (G extends singleParameter ? ActionPayload<P[0],void> : ReturnType<G> extends ActionPayload<void,void> ? {} : ReturnType<G>) } */
export const withMeta =
    getPayload =>
    (meta, ...params) => {
        if (meta && typeof meta === 'object') {
            const { asyncId = uuid() } = meta || {};
            meta.asyncId = asyncId;
        }
        return {
            ...getPayload(...params),
            ...(meta ? { meta } : {}),
        };
    };

/** @type {<G extends (...args: P) => R, E extends object | void>(getPayload: G, prefill: E) => <P extends any[] | void = void>(...params: P) => E & (G extends singleParameter ? ActionPayload<P[0],void> : ReturnType<G> extends ActionPayload<void,void> ? {} : ReturnType<G>) } */
export const withPrefill =
    (getPayload, prefill) =>
    (...params) => {
        if (prefill && typeof prefill === 'object') {
            return deepMerge(prefill, getPayload(...params));
        }
        return getPayload(...params);
    };
